import React, { useState } from "react";
import { Table, Button, Tag, Input, Select, message, Spin, Modal } from "antd";
import { useQuery } from "@tanstack/react-query";
import {
  getAllStampcards,
  approveStampcard,
  rejectStampcard,
} from "../actions/acc_actions";
import FlipCard from "./FlipCard";
const { Option } = Select;

export default function CardApprovalsTab() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState(null);
  const [searchText, setSearchText] = useState("");

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);

  const { data, isLoading, refetch, error } = useQuery({
    queryKey: ["cards", page, pageSize, statusFilter, searchText],
    queryFn: () =>
      getAllStampcards({
        page,
        limit: pageSize,
        status: statusFilter,
        name: searchText,
      }),
    keepPreviousData: true,
  });

  const handleApprove = async (cardId) => {
    try {
      await approveStampcard(cardId);
      message.success("Card approved successfully");
      refetch();
    } catch (err) {
      message.error("Failed to approve card");
    }
  };

  const handleReject = async (cardId) => {
    try {
      await rejectStampcard(cardId);
      message.success("Card rejected successfully");
      refetch();
    } catch (err) {
      message.error("Failed to reject card");
    }
  };

  const handleView = (card) => {
    setSelectedCard(card);
    setModalVisible(true);
  };

  const columns = [
    {
      title: "Cardholder Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <b>{text}</b>,
    },
    {
      title: "Card Number",
      dataIndex: "card_number",
      key: "card_number",
      render: (number) => number || "-",
    },
    {
      title: "Created",
      dataIndex: "created",
      key: "created",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Status",
      dataIndex: "approve_status",
      key: "approve_status",
      render: (status, record) =>
        status === "PENDING" ? (
          <div style={{ display: "flex", gap: "8px" }}>
            <Button type="primary" onClick={() => handleApprove(record.urn)}>
              Approve
            </Button>
            <Button danger onClick={() => handleReject(record.urn)}>
              Reject
            </Button>
          </div>
        ) : (
          <Tag color={status === "ACCEPTED" ? "green" : "red"}>{status}</Tag>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button onClick={() => handleView(record)}>View</Button>
      ),
    },
  ];

  return (
    <Spin spinning={isLoading}>
      <div style={{ marginBottom: 16, display: "flex", gap: "8px" }}>
        <Input
          placeholder="Search by cardholder name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onPressEnter={() => setPage(1)}
        />
        <Select
          placeholder="Filter by status"
          allowClear
          style={{ width: 180 }}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value)}
        >
          <Option value="ACCEPTED">ACCEPTED</Option>
          <Option value="PENDING">PENDING</Option>
          <Option value="REJECTED">REJECTED</Option>
        </Select>
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={data?.body?.stampCards || []}
        loading={isLoading}
        pagination={{
          current: data?.body?.paginations?.currentPage || page,
          pageSize: data?.body?.paginations?.pageSize || pageSize,
          total: data?.body?.paginations?.totalCount || 0,
          onChange: (newPage, newPageSize) => {
            setPage(newPage);
            setPageSize(newPageSize);
          },
          showSizeChanger: true,
        }}
        style={{ width: "100%" }}
        scroll={{ x: "max-content" }}
      />

      {/* Modal for FlipCard */}
      <Modal
        visible={modalVisible}
        title="Card Details"
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {selectedCard &&
          (selectedCard?.rules?.by_portal ? (
            // Normal FlipCard for portal items
            <FlipCard
              className="flipcard"
              business={selectedCard.business}
              item={selectedCard}
            />
          ) : (
            // Static gray card for non-portal items
            <div
              style={{
                background: "#e9e9e9ff",
                position: "relative",
              }}
              className="card-container"
            >
              <h3
                style={{
                  position: "absolute",
                  top: "40%",
                  left: "10%",
                  width: "80%",
                  textAlign: "center",
                }}
                className="card-title"
              >
                {selectedCard.name || "Unnamed"}
              </h3>
            </div>
          ))}
      </Modal>
    </Spin>
  );
}
